import React from 'react';
import { TextInput, required, maxLength, number, regex, minLength } from 'react-admin';
import Autocomplete from '@material-ui/lab/Autocomplete';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { makeStyles } from '@material-ui/core/styles';
import parse from 'autosuggest-highlight/parse';
import throttle from 'lodash/throttle';
import ISConfig from '../config';
import { useField } from 'react-final-form';
import CustomLabelInput from '../components/CustomLabelInput';

function getFormattedCity(sublocality, administrative_area, locality) {
    var _sublocality = (sublocality != null) ? sublocality.long_name + ', ' : '';
    var _locality = (locality != null) ? locality.long_name : '';
    var _administrative_area = (administrative_area != null) ? administrative_area.long_name : '';
    let _city_administrative_area_plus_locality = (_administrative_area == _locality || _locality.length == 0)? _administrative_area : _locality;
    return _sublocality + _city_administrative_area_plus_locality;
}

function loadScript(src, position, id) {
    if (!position) {
        return;
    }

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', id);
    script.src = src;
    position.appendChild(script);
}

const autocompleteService = { current: null };

const useStyles = makeStyles((theme) => ({
    icon: {
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(2),
    },
}));

const CustomAutocompleteNameInput = ({ source }) => {
    const { input: { name, onChange, value }, meta: { touched, error } } = useField(source);

    const classes = useStyles();
    const [v, setValue] = React.useState(null);
    const [inputValue, setInputValue] = React.useState('');
    const [options, setOptions] = React.useState([]);
    const [cityAddress, setCityAddress] = React.useState('');
    const [stateAddress, setStateAddress] = React.useState('');
    const [zipAddress, setZipAddress] = React.useState('');
    const [streetAddress, setStreetAddress] = React.useState('');
    const loaded = React.useRef(false);

    if (typeof window !== 'undefined' && !loaded.current) {
        if (!document.querySelector('#google-maps')) {
            loadScript(
                `https://maps.googleapis.com/maps/api/js?key=${ISConfig.googleAPIkey}&libraries=places&language=en`,
                document.querySelector('head'),
                'google-maps',
            );
        }

        loaded.current = true;
    }

    const fetchPlacePredictions = React.useMemo(
        () =>
            throttle((request, callback) => { autocompleteService.current.getPlacePredictions(request, callback); }, 200),
        [],
    );

    React.useEffect(() => {
        let active = true;

        if (!autocompleteService.current && window.google) { autocompleteService.current = new window.google.maps.places.AutocompleteService(); }
        if (!autocompleteService.current) { return undefined; }
        if (inputValue === '') { setOptions(v ? [v] : []); return undefined; }

        fetchPlacePredictions({ input: inputValue }, (results) => {
            if (active) {
                let newOptions = [];
                if (v) { newOptions = [v]; }
                if (results) { newOptions = [...newOptions, ...results]; }
                setOptions(newOptions);
            }
        });
        return () => { active = false; };
    }, [v, inputValue, fetchPlacePredictions]);

    return (
        <>
            <div className="py-0 my-0 mt-2">
                <Autocomplete
                    validate={required()}
                    className="m-0 p-0"
                    id="google-map-demo"
                    getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
                    filterOptions={(x) => x}
                    options={options}
                    autoComplete
                    includeInputInList
                    filterSelectedOptions
                    value={value}
                    onChange={(event, newValue) => {
                        setOptions(newValue ? [newValue, ...options] : options);
                        setValue(newValue);

                        if (newValue) {
                            var placesService = new google.maps.places.PlacesService(document.createElement('div'));
                            placesService.getDetails({
                                placeId: newValue.place_id
                            }, function (place, status) {
                                if (status === google.maps.places.PlacesServiceStatus.OK) {
                                    onChange(newValue.structured_formatting.main_text);
                                    setStreetAddress(place.formatted_address.split(',')[0]);

                                    setCityAddress(getFormattedCity(
                                        place.address_components.filter(v => v.types.includes('sublocality'))[0],
                                        place.address_components.filter(v => v.types.includes('administrative_area_level_1'))[0],
                                        place.address_components.filter(v => v.types.includes('locality'))[0],
                                    ));

                                    let state = place.address_components.filter(v => v.types.includes('administrative_area_level_1'))[0];
                                    state != null ? setStateAddress(state.short_name) : setStateAddress('');

                                    let postalCode = place.address_components.filter(v => v.types.includes('postal_code'))[0];
                                    postalCode != null ? setZipAddress(postalCode.long_name) : setZipAddress('00000');

                                } else {
                                    setStateAddress('');
                                    setCityAddress('');
                                    setZipAddress('');
                                    setStreetAddress('');
                                }
                            });
                        }
                    }}
                    onInputChange={(event, newInputValue) => {
                        setInputValue(newInputValue);
                    }}
                    renderInput={(params) => {
                        return (
                            <TextInput
                                {...params}
                                source={source}
                                label={<CustomLabelInput label="Name"/>}
                                onChange={onChange}
                                validate={required()}
                                variant="outlined"
                                className="m-0 p-0"
                            />
                        )
                    }}
                    renderOption={(option) => {
                        const matches = option.structured_formatting.main_text_matched_substrings;
                        const parts = parse(
                            option.structured_formatting.main_text,
                            matches.map((match) => [match.offset, match.offset + match.length]),
                        );

                        return (
                            <Grid container alignItems="center">
                                <Grid item>
                                    <LocationOnIcon className={classes.icon} />
                                </Grid>
                                <Grid item xs>
                                    {parts.map((part, index) => (
                                        <span key={index} style={{ fontWeight: part.highlight ? 700 : 400 }}>
                                            {part.text}
                                        </span>
                                    ))}

                                    <Typography variant="body2" color="textSecondary">
                                        {option.structured_formatting.secondary_text}
                                    </Typography>
                                </Grid>
                            </Grid>
                        );
                    }}
                />
            </div>
            <div>
                <CustomAutocompleteInput source="address.street" label={<CustomLabelInput label="Street"/>} setvalue={streetAddress} validate={required()} variant="outlined" />
                <CustomAutocompleteInput source="address.city" label={<CustomLabelInput label="City"/>} setvalue={cityAddress} validate={required()} variant="outlined" />
                <CustomAutocompleteInput source="address.state" label={<CustomLabelInput label="State"/>} setvalue={stateAddress} validate={[required(), minLength(2), maxLength(2)]} variant="outlined" />
                <CustomAutocompleteInput source="address.zip" validate={[required(), regex(/^\d{5}$/, 'Must be a valid Zip Code')]} label={<CustomLabelInput label="Zip"/>} setvalue={zipAddress} fullWidth variant="outlined" />
            </div>
        </>
    );
}

const CustomAutocompleteInput = ({ source, label, setvalue, validate, variant }) => {
    const { input: { name, onChange, value }, meta: { touched, error } } = useField(source);

    return (
        <TextInput
            className="d-none"
            source={source}
            label={label}
            value={setvalue}
            onChange={onChange(setvalue || value)}
            validate={validate}
            variant={variant}
            fullWidth
        />
    )
}

const CustomNameLocationInput = ({ record }) => {
    return ( <CustomAutocompleteNameInput source="name" /> );
};

export default CustomNameLocationInput;